/**
 * @module builder
 */
import type {
  LegacyTieredPackageFeature,
  TieredPackageFeature,
  TieredPackageFeatures,
  TieredPackageTier,
  TieredPackageTierId
} from 'paintscout'
import { uuid } from '../../util'
import { TIER_ORDER } from './default-tiered-packages'

/**
 * Feature rows are per tier and nothing else (ENG-1783).
 *
 * Each tier holds its own list. Adding, wording, reordering, or removing a row changes that tier
 * and no other, and a row has no identity across tiers — two tiers that both say "2 coats" hold two
 * separate rows. A row still carries `included`, so a tier can list something it does not include
 * and show it crossed out; that is the row's own flag, not a per-tier map.
 *
 * Two older shapes are still in stored documents. Both are read here and neither is written:
 *  - shared rows: one `features` array for the whole set, with `included`/`labels` keyed by tier;
 *  - the oldest form: a plain `string[]` on each tier.
 * getTieredPackageFeatures splits both into per-tier lists, so every caller sees one shape.
 */

export type TieredPackageFeatureSource = {
  features?: TieredPackageFeatures | LegacyTieredPackageFeature[]
  tiers: Record<TieredPackageTierId, TieredPackageTier>
}

/** Every tier keyed, so callers can index any tier without a null check. */
export type TieredPackageFeatureMap = Record<TieredPackageTierId, TieredPackageFeature[]>

const emptyFeatureMap = (): TieredPackageFeatureMap =>
  TIER_ORDER.reduce((acc, tierId) => ({ ...acc, [tierId]: [] }), {} as TieredPackageFeatureMap)

/**
 * A blank row for one tier's list — what "Add Feature" creates. It starts ticked, and only on the
 * tier it was added from.
 */
export function createTieredPackageFeature(label = ''): TieredPackageFeature {
  return { id: uuid(), label, included: true }
}

/** A stored row with its inclusion resolved. An absent flag reads as included. */
const normalizeFeature = (feature: TieredPackageFeature): TieredPackageFeature => ({
  ...feature,
  included: feature.included !== false
})

/**
 * Split the superseded shared rows into per-tier lists.
 *
 * A row lands on the tiers its `included` map named, with the wording that tier was showing — its
 * own from `labels`, or the row's `label`. A row that named no tier was seeded from its `origin`
 * when read, so it goes on every tier the same way: ticked from the origin up, crossed out below.
 * Each tier gets its own copy with its own id, because the copies no longer have anything to do
 * with each other.
 */
function splitSharedFeatures(features: LegacyTieredPackageFeature[]): TieredPackageFeatureMap {
  const map = emptyFeatureMap()

  features.forEach((feature) => {
    const originIndex = feature.origin ? TIER_ORDER.indexOf(feature.origin) : -1
    TIER_ORDER.forEach((tierId, index) => {
      const included = feature.included?.[tierId]
      if (feature.included && included === undefined) {
        return
      }
      map[tierId].push({
        id: uuid(),
        label: feature.labels?.[tierId] ?? feature.label ?? '',
        included: feature.included ? included === true : originIndex < 0 || index >= originIndex
      })
    })
  })

  return map
}

/**
 * The oldest shape: a `string[]` on each tier. Each tier's own list becomes its own rows, all
 * ticked — which is all those lists ever said.
 */
function migrateLegacyFeatures(tiers: Record<TieredPackageTierId, TieredPackageTier>): TieredPackageFeatureMap {
  return TIER_ORDER.reduce(
    (acc, tierId) => ({
      ...acc,
      [tierId]: (tiers[tierId]?.features ?? []).map((label) => ({ id: uuid(), label, included: true }))
    }),
    {} as TieredPackageFeatureMap
  )
}

/** Whether a tier's stored rows already carry a resolved flag, and so need no filling in. */
const isNormalized = (rows?: TieredPackageFeature[]): boolean =>
  !!rows && rows.every((feature) => typeof feature.included === 'boolean')

/**
 * Every tier's feature rows, keyed by tier, migrating either older shape when needed. Narrow to one
 * tier with getTierFeatures.
 *
 * Rows already in the new shape are handed back as they are stored — the same object, and the same
 * array per tier. This is called on every render of both editors and of the customer's cards, so a
 * saved package set must not allocate a new map each time and re-render everything below it.
 */
export function getTieredPackageFeatures(source: TieredPackageFeatureSource): TieredPackageFeatureMap {
  if (Array.isArray(source.features)) {
    return splitSharedFeatures(source.features)
  }
  if (source.features) {
    const stored = source.features
    if (TIER_ORDER.every((tierId) => isNormalized(stored[tierId]))) {
      return stored as TieredPackageFeatureMap
    }
    return TIER_ORDER.reduce(
      (acc, tierId) => ({
        ...acc,
        [tierId]: isNormalized(stored[tierId]) ? stored[tierId] : (stored[tierId] ?? []).map(normalizeFeature)
      }),
      {} as TieredPackageFeatureMap
    )
  }
  return migrateLegacyFeatures(source.tiers)
}

/** One tier's rows, each with its wording and whether it shows a checkmark or an X. */
export function getTierFeatures(
  source: TieredPackageFeatureSource,
  tierId: TieredPackageTierId
): TieredPackageFeature[] {
  return getTieredPackageFeatures(source)[tierId]
}

/**
 * The whole map with one tier's rows replaced — how an editor writes a tier's list back. It reads
 * the other tiers through getTieredPackageFeatures, so writing one tier also lands the migration of
 * the rest rather than leaving half an old document behind.
 */
export function setTierFeatures(
  source: TieredPackageFeatureSource,
  tierId: TieredPackageTierId,
  features: TieredPackageFeature[]
): TieredPackageFeatureMap {
  return { ...getTieredPackageFeatures(source), [tierId]: features }
}

/** Whether a row says anything — a row added, then left untyped, says nothing. */
export function hasFeatureText(feature: TieredPackageFeature): boolean {
  return !!feature.label?.trim()
}

/**
 * Every tier's list with its blank rows dropped (ENG-1785) — a row left untyped has nothing to show
 * a customer, so it is not saved.
 */
export function dropBlankFeatures(features: TieredPackageFeatureMap): TieredPackageFeatureMap {
  return TIER_ORDER.reduce(
    (acc, tierId) => ({ ...acc, [tierId]: (features[tierId] ?? []).filter(hasFeatureText) }),
    {} as TieredPackageFeatureMap
  )
}

/** Remove a row from one tier's list. No other tier holds this row. */
export function removeTieredPackageFeature(features: TieredPackageFeature[], id: string): TieredPackageFeature[] {
  return features.filter((feature) => feature.id !== id)
}

/** Mark a row included (checkmark) or not included (greyed X). */
export function setTieredPackageFeatureIncluded(
  features: TieredPackageFeature[],
  id: string,
  included: boolean
): TieredPackageFeature[] {
  return features.map((feature) => (feature.id === id ? { ...feature, included } : feature))
}

/** Word a row. */
export function setTieredPackageFeatureLabel(
  features: TieredPackageFeature[],
  id: string,
  label: string
): TieredPackageFeature[] {
  return features.map((feature) => (feature.id === id ? { ...feature, label } : feature))
}

/**
 * Package descriptions are plain text, but the quote-level setup wizard used to author them
 * with the rich-text editor, so saved values can still be TipTap HTML. Rendering those as text
 * would show raw markup to the customer, and rendering them as HTML is an injection sink — so
 * flatten to text: block ends become newlines, tags go, entities decode.
 */
export function getTieredPackageDescriptionText(description?: string): string {
  if (!description) {
    return ''
  }
  if (!/<[a-z/][\s\S]*>/i.test(description)) {
    return description
  }
  return description
    .replaceAll(/<br\s*\/?>/gi, '\n')
    .replaceAll(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replaceAll(/<[^>]+>/g, '')
    .replaceAll(/&nbsp;/gi, ' ')
    .replaceAll(/&amp;/gi, '&')
    .replaceAll(/&lt;/gi, '<')
    .replaceAll(/&gt;/gi, '>')
    .replaceAll(/&quot;/gi, '"')
    .replaceAll('&#39;', "'")
    .replaceAll(/\n{3,}/g, '\n\n')
    .trim()
}

/** Move a row to sit where another row in the same tier's list currently is (drag reorder). */
export function reorderTieredPackageFeatures(
  features: TieredPackageFeature[],
  draggedId: string,
  targetId: string
): TieredPackageFeature[] {
  if (draggedId === targetId) {
    return features
  }
  const next = [...features]
  const from = next.findIndex((f) => f.id === draggedId)
  const to = next.findIndex((f) => f.id === targetId)
  if (from < 0 || to < 0) {
    return features
  }
  const [row] = next.splice(from, 1)
  next.splice(to, 0, row)
  return next
}
